import { useRef, useState } from 'react'

/**
 * Where each person sits in a section. Everyone keeps their place, and
 * anyone new joins the end. A card's slot stays put when the person in it
 * changes, so the card isn't rebuilt or moved.
 */
const usePlaces = (memberIds: number[]) => {
  const [moving, setMoving] = useState<{
    leaving: number[]
    arriving: number[]
  }>({ leaving: [], arriving: [] })
  const order = useRef<number[]>([])
  // the slot each person's card is in, when it isn't their own id
  const slots = useRef(new Map<number, number>())

  const isShown = (id: number) =>
    (memberIds.includes(id) && !moving.leaving.includes(id)) ||
    moving.arriving.includes(id)

  order.current = [
    ...order.current.filter(isShown),
    ...[...memberIds, ...moving.arriving].filter(
      (id, index, ids) =>
        isShown(id) && !order.current.includes(id) && ids.indexOf(id) === index
    ),
  ]

  const slotFor = (id: number) => slots.current.get(id) ?? id

  const claimSlot = (personId: number, slot: number) => {
    slots.current.set(personId, slot)
  }

  /**
   * Puts someone into a slot, in place of who was in it. They're shown there
   * straight away, while it saves.
   */
  const moveInto = async (
    slot: number,
    personId: number,
    { replacing, save }: { replacing?: number; save: () => Promise<unknown> }
  ) => {
    claimSlot(personId, slot)
    if (replacing)
      order.current = order.current.map((id) =>
        id === replacing ? personId : id
      )
    setMoving((current) => ({
      leaving: replacing ? [...current.leaving, replacing] : current.leaving,
      arriving: [...current.arriving, personId],
    }))

    try {
      await save()
    } catch (error) {
      if (replacing)
        order.current = order.current.map((id) =>
          id === personId ? replacing : id
        )
      throw error
    } finally {
      setMoving((current) => ({
        leaving: current.leaving.filter((id) => id !== replacing),
        arriving: current.arriving.filter((id) => id !== personId),
      }))
    }
  }

  return { shownIds: order.current, slotFor, claimSlot, moveInto }
}

export default usePlaces
